import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Send, CheckCircle, HardHat, Building2, Briefcase, MessageSquare } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  const limitedNumber = phoneNumber.slice(0, 10);
  
  if (limitedNumber.length === 0) return '';
  if (limitedNumber.length <= 3) return `(${limitedNumber}`;
  if (limitedNumber.length <= 6) return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
  return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
};

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    sms_consent: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Bot protection: Track when form was loaded
  const [formLoadTime] = useState(Date.now());
  // Honeypot field (hidden from users, bots will fill it)
  const [honeypot, setHoneypot] = useState('');

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection checks
    const timeSpent = Date.now() - formLoadTime;
    
    // Check 1: If honeypot is filled, it's a bot (silently reject)
    if (honeypot) {
      console.log('Bot detected: honeypot filled');
      // Fake success to not alert bots
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', sms_consent: false });
      }, 3000);
      return;
    }
    
    // Check 2: If form submitted in less than 3 seconds, likely a bot
    if (timeSpent < 3000) {
      console.log('Bot detected: form submitted too fast');
      toast.error('Please take your time filling out the form.', {
        description: 'Form submitted too quickly. Please try again.',
        duration: 5000,
      });
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to general contact endpoint with bot protection data
      await axios.post(`${BACKEND_URL}/api/contact/general`, {
        ...formData,
        _gotcha: honeypot,  // Honeypot field
        _timestamp: formLoadTime  // When form was loaded
      });
      setIsSubmitted(true);
      
      // Show success toast
      toast.success('Message Sent Successfully!', {
        description: 'Thank you for reaching out. We\'ll respond to your inquiry as soon as possible.',
        duration: 5000,
      });
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          sms_consent: false
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMsg = error.response?.data?.detail || 'There was an error submitting your message. Please try again or call us directly.';
      toast.error('Submission Failed', {
        description: errorMsg,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const ctaOptions = [
    {
      title: 'Project Consultation',
      description: 'Ready to start your construction or renovation project? Request a free consultation.',
      icon: HardHat,
      link: '/consultation',
      buttonText: 'Request Consultation',
      color: 'bg-cyan-600 hover:bg-cyan-700'
    },
    {
      title: 'Vendor Application',
      description: 'Interested in becoming a vendor or subcontractor partner with C3 Contracting?',
      icon: Building2,
      link: '/vendor-application',
      buttonText: 'Apply as Vendor',
      color: 'bg-slate-700 hover:bg-slate-800'
    },
    {
      title: 'Employment Application',
      description: 'Looking for a career in construction? Join our team of skilled professionals.',
      icon: Briefcase,
      link: '/employment-application',
      buttonText: 'Apply for Employment',
      color: 'bg-slate-700 hover:bg-slate-800'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <MessageSquare className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Have a question or need assistance? We're here to help. Choose an option below or send us a general message.
          </p>
        </div>
      </section>

      {/* CTA Options Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">How Can We Help You?</h2>
            <p className="text-slate-600">Select the option that best fits your needs</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {ctaOptions.map((option) => (
              <div key={option.title} className="bg-slate-50 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <option.icon className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{option.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{option.description}</p>
                <Link to={option.link}>
                  <Button className={`${option.color} text-white font-medium w-full`}>
                    {option.buttonText}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Contact Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-cyan-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">General Inquiry</h2>
              </div>
              <p className="text-slate-600 mb-8">
                Have a question that doesn't fit the options above? Send us a message and we'll get back to you soon.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-slate-600 hover:text-cyan-600">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-600">Mon - Fri: 8:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* General Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-slate-600">
                      Thank you for reaching out. We'll respond to your inquiry as soon as possible.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from humans, bots will fill it */}
                    <div 
                      style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        opacity: 0, 
                        height: 0, 
                        width: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none'
                      }} 
                      aria-hidden="true"
                    >
                      <label htmlFor="website">Website URL</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 mb-2 block">Your Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700 mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 mb-2 block">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`h-12 ${errors.phone ? 'border-red-500' : ''}`}
                          maxLength={14}
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-slate-700 mb-2 block">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 mb-2 block">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="How can we help you?"
                      />
                    </div>

                    {/* SMS Consent Checkbox */}
                    {formData.phone && (
                      <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <input
                          type="checkbox"
                          id="sms_consent"
                          name="sms_consent"
                          checked={formData.sms_consent}
                          onChange={handleChange}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <label htmlFor="sms_consent" className="text-sm text-slate-600 leading-relaxed">
                          I agree to receive SMS/text messages from C3 Contracting regarding my inquiry. 
                          Message & data rates may apply. Reply STOP to unsubscribe at any time.
                        </label>
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold h-12"
                    >
                      {isSubmitting ? 'Sending...' : <><Send className="mr-2 w-5 h-5" /> Send Message</>}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
